import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import DateTimePickerDays from './DateTimePickerDays';
import DateTimePickerMonths from './DateTimePickerMonths';
import DateTimePickerYears from './DateTimePickerYears';
import moment from 'moment';

class DateTimePickerDate extends Component {
  constructor(props) {
    super(props);
    const viewModes = {
      days: {
        daysDisplayed: true,
        monthsDisplayed: false,
        yearsDisplayed: false,
      },
      months: {
        daysDisplayed: false,
        monthsDisplayed: true,
        yearsDisplayed: false,
      },
      years: {
        daysDisplayed: false,
        monthsDisplayed: false,
        yearsDisplayed: true,
      },
    };

    this.state = {
      ...(viewModes[this.props.viewMode] ||
        viewModes[Object.keys(viewModes)[this.props.viewMode]] ||
        viewModes.days),
      onFocusDate: moment(props.selectedDate, props.format, true),
      firstDate: moment(),
      lastDate: moment(),
    };
  }

  componentDidMount() {
    ReactDOM.findDOMNode(this.refs.datepickerref).addEventListener(
      'keydown',
      this.onKeyDown
    );

    if (this.props.onShow) {
      if (this.state.daysDisplayed) {
        this.calcFirstAndLastDay();
      } else if (this.state.monthsDisplayed) {
        this.updateMonthFocus();
      } else if (this.state.yearsDisplayed) {
        this.updateYearFocus();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { format, minDate, onShow, selectedDate } = this.props;

    // The datepicker has just become visible & rendered
    if (onShow && !prevProps.onShow) {
      // ensure focus date matches selected date
      // but user can type an invalid date in the text field
      let dateToInitiallyFocus = moment(selectedDate, format, true);

      if (selectedDate.isBefore(minDate, 'day')) {
        dateToInitiallyFocus = moment(minDate, format, true);
      }

      this.setState(
        {
          onFocusDate: dateToInitiallyFocus,
        },
        () => {
          this.calcFirstAndLastDay();
        }
      );

      if (this.state.monthsDisplayed) {
        this.updateMonthFocus();
      } else if (this.state.yearsDisplayed) {
        this.updateYearFocus();
      }
    }

    if (onShow) {
      if (!this.props.viewDate.isSame(prevProps.viewDate, 'day')) {
        // Need to recalc firstDay & last Day
        this.calcFirstAndLastDay();
      }

      if (this.state.daysDisplayed) {
        if (!prevState.daysDisplayed) {
          this.calcFirstAndLastDay();
        }
        this.updateDayFocus();
      }
      if (this.state.monthsDisplayed) {
        this.updateMonthFocus();
      }
      if (this.state.yearsDisplayed) {
        // we may be on a new page of years
        const { firstYear, lastYear } = this.calculateFirstLastYearsOnPage();

        const yearOnPage = this.state.onFocusDate.isBetween(
          `01/01/${firstYear}`,
          `01/01/${lastYear}`,
          'year'
        );

        if (!yearOnPage) {
          this.setState(
            {
              onFocusDate: this.props.viewDate,
            },
            () => {
              this.updateYearFocus();
            }
          );
        }
      }
    }
  }

  calcFirstAndLastDay = () => {
    // don't calculate if the calendar isn't displaying days
    if (!this.state.daysDisplayed) return;

    const tds = ReactDOM.findDOMNode(
      this.refs.datepickerref
    ).getElementsByTagName('td');

    // calculate the first and last day to show
    const firstDay = tds.item(0)?.textContent;
    const lastDay = tds.item(tds.length - 1)?.textContent;

    let y = this.props.viewDate.year();
    let m = this.props.viewDate.month(); // moment uses array indicing for months
    let firstDate, lastDate;

    if (m > 0 || m < 11) {
      firstDate = moment()
        .year(y)
        .month(m - 1)
        .date(firstDay);
      lastDate = moment()
        .year(y)
        .month(m + 1)
        .date(lastDay);
    } else if (m === 0) {
      // Jan
      firstDate = moment()
        .year(y - 1)
        .month(12)
        .date(firstDay);
      lastDate = moment()
        .year(y)
        .month(m + 1)
        .date(lastDay);
    } else if (m === 11) {
      // Dec
      firstDate = moment()
        .year(y)
        .month(m - 1)
        .date(firstDay);
      lastDate = moment()
        .year(y + 1)
        .month(0)
        .date(lastDay);
    }
    this.setState({
      firstDate,
      lastDate,
    });

    this.updateDayFocus();
  };

  addWeekOrDayForFocus = (addWeek = false) => {
    const addDays = addWeek ? 7 : 1;

    const proposedFocusDate = this.state.onFocusDate.clone().add(addDays, 'd');

    // ensure we don't allow dates before max date
    if (proposedFocusDate.isAfter(this.props.maxDate, 'day')) {
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        if (this.state.onFocusDate.isSameOrAfter(this.state.lastDate)) {
          this.props.addMonth();
        }
        this.updateDayFocus();
      }
    );
  };

  // subtrack a day or a week from the current onFocusDate
  subtractWeekOrDayForFocus = (subtractWeek = false) => {
    const subtractDays = subtractWeek ? 7 : 1;

    const proposedFocusDate = this.state.onFocusDate
      .clone()
      .subtract(subtractDays, 'd');

    // ensure we don't allow dates before min date
    if (proposedFocusDate.isBefore(this.props.minDate, 'day')) {
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        if (this.state.onFocusDate.isBefore(this.state.firstDate, 'day')) {
          this.props.subtractMonth();
        }
        this.updateDayFocus();
      }
    );
  };

  addMonthOrQuarterForFocus = (addQuarter = false) => {
    const addMonths = addQuarter ? 4 : 1;

    const proposedFocusDate = this.state.onFocusDate
      .clone()
      .add(addMonths, 'M');

    // ensure we don't allow dates before max date
    if (proposedFocusDate.isAfter(this.props.maxDate, 'month')) {
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        if (this.state.onFocusDate.month() === 0) {
          this.props.addYear();
        }
        this.updateMonthFocus();
      }
    );
  };

  // subtrack a month or a quarter (4 month) from the current onFocusDate
  subtractMonthOrQuarterForFocus = (subtractQuarter = false) => {
    const subtractMonths = subtractQuarter ? 4 : 1;

    const proposedFocusDate = this.state.onFocusDate
      .clone()
      .subtract(subtractMonths, 'M');

    // ensure we don't allow dates before min date
    if (proposedFocusDate.isBefore(this.props.minDate, 'month')) {
      /// hhmmm not sure here
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        // check if we are back a calendar month
        if (this.state.onFocusDate.month() === 11) {
          this.props.subtractYear();
        }
        this.updateMonthFocus();
      }
    );
  };

  addYearOrQuadrennialForFocus = (addQuadrennial = false) => {
    const addYears = addQuadrennial ? 4 : 1;

    const proposedFocusDate = this.state.onFocusDate.clone().add(addYears, 'Y');

    // ensure we don't allow dates before max date
    if (proposedFocusDate.isAfter(this.props.maxDate, 'year')) {
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        const { firstYear, lastYear } = this.calculateFirstLastYearsOnPage();

        if (this.state.onFocusDate.isBefore(firstYear, 'year')) {
          this.props.subtractYear();
        } else if (this.state.onFocusDate.isAfter(lastYear, 'year')) {
          this.props.subtractYear();
        }
        this.updateYearFocus();
      }
    );
  };

  subtractYearOrQuadrennialForFocus = (subtractQuadrennial = false) => {
    const subtractYears = subtractQuadrennial ? 4 : 1;

    const proposedFocusDate = this.state.onFocusDate
      .clone()
      .subtract(subtractYears, 'Y');

    // ensure we don't allow dates before min date
    if (proposedFocusDate.isBefore(this.props.minDate, 'year')) {
      return;
    }

    this.setState(
      {
        onFocusDate: proposedFocusDate,
      },
      () => {
        // check if we are back a calendar page
        this.updateYearFocus();
      }
    );
  };

  updateDayFocus = () => {
    const selectedDay = this.state.onFocusDate.date().toString();

    // we know that the table has all the month cells and the first week of next month, but they
    // have classes to distinguish
    const tds = ReactDOM.findDOMNode(
      this.refs.datepickerref
    ).getElementsByTagName('td');

    let dayDomNodeToFocus;
    let inOld = false; // date to focus on is in last month
    let inNew = false; // date to focus on is in next month

    const focusMonth = this.state.onFocusDate.month();
    const focusYear = this.state.onFocusDate.year();
    const viewDateMonth = this.props.viewDate.month();
    const viewDateYear = this.props.viewDate.year();

    if (focusYear === viewDateYear) {
      if (focusMonth < viewDateMonth) {
        inOld = true;
      } else if (focusMonth > viewDateMonth) {
        inNew = true;
      }
    } else if (focusYear > viewDateYear) {
      // calendar page is straddling a year
      if (focusMonth < viewDateMonth) {
        inNew = true;
      }
    } else if (focusYear < viewDateYear) {
      inOld = true;
    }

    for (let td of tds) {
      if (td.classList.contains('disabled')) continue;
      if (!inOld && td.classList.contains('old')) continue;
      if (!inNew && td.classList.contains('new')) continue;

      if (
        td.textContent === selectedDay &&
        ((inOld && td.classList.contains('old')) ||
          (inNew && td.classList.contains('new')))
      ) {
        dayDomNodeToFocus = td;
        break;
      }

      if (td.textContent === selectedDay && !inOld && !inNew) {
        dayDomNodeToFocus = td;
        break;
      }
    }

    if (dayDomNodeToFocus) {
      dayDomNodeToFocus.focus();
    } else {
      console.warn('Focus not placed on date');
    }
  };

  updateMonthFocus = () => {
    const selectedMonth = this.state.onFocusDate.month();

    const monthDomNodes = ReactDOM.findDOMNode(
      this.refs.datepickerref
    ).querySelectorAll('[data-test-id="datepicker-month--cell"]');

    const monthDomNodeToFocus = monthDomNodes.item(selectedMonth);

    if (monthDomNodeToFocus) {
      monthDomNodeToFocus.focus();
    } else {
      console.warn('Focus not placed on date');
    }
  };

  updateYearFocus = () => {
    const selectedYear = this.state.onFocusDate.year();
    const { firstYear } = this.calculateFirstLastYearsOnPage();

    const yearDomNodes = ReactDOM.findDOMNode(
      this.refs.datepickerref
    ).querySelectorAll('[data-test-id="datepicker-year--cell"]');

    const yearDomNodeToFocus = yearDomNodes.item(selectedYear - firstYear);

    if (yearDomNodeToFocus) {
      yearDomNodeToFocus.focus();
    } else {
      console.warn('Focus not placed on date');
    }
  };

  calculateFirstLastYearsOnPage = () => {
    const firstYear = parseInt(this.props.viewDate.year() / 10, 10) * 10 - 1;
    return { firstYear, lastYear: firstYear + 12 };
  };

  onKeyDown = (event) => {
    const { onClose, onShow } = this.props;

    let stopPropagation = false;

    if (!onShow) {
      return;
    }

    switch (event.key) {
      case 'Esc':
      case 'Escape':
        if (onClose) {
          onClose();
        }
        break;
      case ' ':
      case 'Enter':
        this.handleSpaceOrEnterKey(event);
        break;
      case 'Right':
      case 'ArrowRight':
        this.handleRightOrDownKey(false);
        stopPropagation = true;
        break;
      case 'Left':
      case 'ArrowLeft':
        this.handleLeftOrUpKey(false);
        stopPropagation = true;
        break;
      case 'Down':
      case 'ArrowDown':
        this.handleRightOrDownKey(true);
        stopPropagation = true;
        break;

      case 'Up':
      case 'ArrowUp':
        this.handleLeftOrUpKey(true);
        stopPropagation = true;
        break;

      case 'Tab':
        break;
      default:
        break;
    }

    if (stopPropagation) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // left or up key decreases the focus date
  handleLeftOrUpKey = (isUpKey = false) => {
    const { daysDisplayed, monthsDisplayed, yearsDisplayed } = this.state;

    if (daysDisplayed) {
      this.subtractWeekOrDayForFocus(isUpKey);
    }

    if (monthsDisplayed) {
      this.subtractMonthOrQuarterForFocus(isUpKey);
    }
    if (yearsDisplayed) {
      this.subtractYearOrQuadrennialForFocus(isUpKey);
    }
  };

  // right or down key increases the focus date
  handleRightOrDownKey = (isDownKey = false) => {
    const { daysDisplayed, monthsDisplayed, yearsDisplayed } = this.state;

    if (daysDisplayed) {
      this.addWeekOrDayForFocus(isDownKey);
    }

    if (monthsDisplayed) {
      this.addMonthOrQuarterForFocus(isDownKey);
    }
    if (yearsDisplayed) {
      this.addYearOrQuadrennialForFocus(isDownKey);
    }
  };

  handleSpaceOrEnterKey = (event) => {
    const domELem = event.target;

    switch (domELem?.dataset?.testId) {
      case 'datepicker-day--cell':
        this.props.setSelectedDate(event);
        break;
      case 'datepicker-month--cell':
        this.setViewMonth(event);
        break;
      case 'datepicker-year--cell':
        this.setViewYear(event);
        this.setState({
          onFocusDate: this.props.viewDate,
        });
        break;
      case 'datepicker-prev--decade':
        this.props.subtractDecade();
        break;
      case 'datepicker-next--decade':
        this.props.addDecade();
        break;
      case 'datepicker-prev--year':
        this.props.subtractYear();
        break;
      case 'datepicker-next--year':
        this.props.addYear();
        break;
      case 'datepicker-prev--month':
        this.props.subtractMonth();
        break;
      case 'datepicker-next--month':
        this.props.addMonth();
        break;
      case 'datepicker-show--months':
        this.showMonths();
        this.updateMonthFocus();
        break;
      case 'datepicker-show--years':
        this.showYears();
        this.updateYearFocus();
        break;
    }
  };

  showMonths = () => {
    return this.setState({
      daysDisplayed: false,
      monthsDisplayed: true,
    });
  };

  showYears = () => {
    return this.setState({
      monthsDisplayed: false,
      yearsDisplayed: true,
    });
  };

  setViewYear = (e) => {
    this.props.setViewYear(e.target.innerHTML);
    return this.setState({
      yearsDisplayed: false,
      monthsDisplayed: true,
    });
  };

  setViewMonth = (e) => {
    this.props.setViewMonth(e.target.innerHTML);
    return this.setState({
      monthsDisplayed: false,
      daysDisplayed: true,
    });
  };

  renderDays = () => {
    if (this.state.daysDisplayed) {
      return (
        <DateTimePickerDays
          addMonth={this.props.addMonth}
          daysOfWeekDisabled={this.props.daysOfWeekDisabled}
          maxDate={this.props.maxDate}
          minDate={this.props.minDate}
          onShow={this.props.onShow}
          selectedDate={this.props.selectedDate}
          setSelectedDate={this.props.setSelectedDate}
          showMonths={this.showMonths}
          showToday={this.props.showToday}
          subtractMonth={this.props.subtractMonth}
          viewDate={this.props.viewDate}
        />
      );
    } else {
      return null;
    }
  };

  renderMonths = () => {
    if (this.state.monthsDisplayed) {
      return (
        <DateTimePickerMonths
          addYear={this.props.addYear}
          maxDate={this.props.maxDate}
          minDate={this.props.minDate}
          onShow={this.props.onShow}
          selectedDate={this.props.selectedDate}
          setViewMonth={this.setViewMonth}
          showYears={this.showYears}
          subtractYear={this.props.subtractYear}
          viewDate={this.props.viewDate}
        />
      );
    } else {
      return null;
    }
  };

  renderYears = () => {
    if (this.state.yearsDisplayed) {
      return (
        <DateTimePickerYears
          addDecade={this.props.addDecade}
          maxDate={this.props.maxDate}
          minDate={this.props.minDate}
          onShow={this.props.onShow}
          selectedDate={this.props.selectedDate}
          setViewYear={this.setViewYear}
          subtractDecade={this.props.subtractDecade}
          viewDate={this.props.viewDate}
        />
      );
    } else {
      return null;
    }
  };

  render() {
    return (
      <div
        className="datepicker"
        ref="datepickerref"
        data-test-id="datepicker-wrapper"
      >
        {this.renderDays()}

        {this.renderMonths()}

        {this.renderYears()}
      </div>
    );
  }
}

DateTimePickerDate.propTypes = {
  subtractMonth: PropTypes.func.isRequired,
  addDay: PropTypes.func.isRequired,
  addMonth: PropTypes.func.isRequired,
  viewDate: PropTypes.object.isRequired,
  selectedDate: PropTypes.object.isRequired,
  showToday: PropTypes.bool,
  viewMode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  daysOfWeekDisabled: PropTypes.array,
  setSelectedDate: PropTypes.func.isRequired,
  subtractYear: PropTypes.func.isRequired,
  addYear: PropTypes.func.isRequired,
  setViewMonth: PropTypes.func.isRequired,
  setViewYear: PropTypes.func.isRequired,
  addDecade: PropTypes.func.isRequired,
  subtractDecade: PropTypes.func.isRequired,
  subtractWeek: PropTypes.func.isRequired,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  onShow: PropTypes.bool,
  onClose: PropTypes.func,
};

export default DateTimePickerDate;
